<script setup lang="ts">
  import { ref, watch, onMounted } from 'vue';
  import ThemeColorFields from '@/shared/ui/inputs/ThemeColorFields.vue';
  import ThemePreviewCard from '@/shared/ui/inputs/ThemePreviewCard.vue';

  const props = defineProps({
    PrimaryColor: {
      type: String,
      default: '#F8F4F4',
    },
    SecondaryColor: {
      type: String,
      default: '#854149',
    },
    TextColor: {
      type: String,
      default: '#2F2F2F',
    },
    PrimaryColorDark: {
      type: String,
      default: '#1E1E1E',
    },
    TextColorDark: {
      type: String,
      default: '#FFFFFF',
    },
    initialPrimaryColor: {
      type: String,
      default: '',
    },
    initialSecondaryColor: {
      type: String,
      default: '',
    },
    initialTextColor: {
      type: String,
      default: '',
    },
    initialPrimaryColorDark: {
      type: String,
      default: '',
    },
    initialSecondaryColorDark: {
      type: String,
      default: '',
    },
    initialTextColorDark: {
      type: String,
      default: '',
    },
  });

  const emit = defineEmits(['colorsChanged']);

  const primaryColor = ref(props.initialPrimaryColor || props.PrimaryColor);
  const secondaryColor = ref(props.initialSecondaryColor || props.SecondaryColor);
  const textColor = ref(props.initialTextColor || props.TextColor);
  const primaryColorDark = ref(props.initialPrimaryColorDark || props.PrimaryColorDark);
  const secondaryColorDark = ref(props.initialSecondaryColorDark || secondaryColor.value);
  const textColorDark = ref(props.initialTextColorDark || props.TextColorDark);

  const updateColors = () => {
    emit('colorsChanged', {
      primaryColor: primaryColor.value,
      secondaryColor: secondaryColor.value,
      textColor: textColor.value,
      primaryColorDark: primaryColorDark.value,
      secondaryColorDark: secondaryColorDark.value,
      textColorDark: textColorDark.value,
    });
  };

  const syncFromProp = (source: () => string, target: typeof primaryColor) => {
    watch(source, (newValue) => {
      if (newValue) {
        target.value = newValue;
      }
    });
  };

  syncFromProp(() => props.initialPrimaryColor, primaryColor);
  syncFromProp(() => props.initialSecondaryColor, secondaryColor);
  syncFromProp(() => props.initialTextColor, textColor);
  syncFromProp(() => props.initialPrimaryColorDark, primaryColorDark);
  syncFromProp(() => props.initialSecondaryColorDark, secondaryColorDark);
  syncFromProp(() => props.initialTextColorDark, textColorDark);

  // Until the branch has its own dark secondary, or the admin picks one, the dark secondary
  // follows the light one. The edit page hydrates the light colors after mount, so this
  // keeps the two in step instead of freezing the pre-load placeholder.
  const darkSecondaryEdited = ref(false);
  watch(secondaryColor, (newValue) => {
    if (!props.initialSecondaryColorDark && !darkSecondaryEdited.value) {
      secondaryColorDark.value = newValue;
    }
  });
  const handleDarkSecondaryInput = (value: string) => {
    darkSecondaryEdited.value = true;
    secondaryColorDark.value = value;
  };

  watch(
    [primaryColor, secondaryColor, textColor, primaryColorDark, secondaryColorDark, textColorDark],
    () => {
      updateColors();
    }
  );

  onMounted(() => {
    updateColors();
  });
</script>

<template>
  <div class="flex flex-col gap-6">
    <section
      class="flex flex-col gap-4"
      data-testid="theme-light-section"
    >
      <div class="text-left font-semibold">
        Modo claro
      </div>
      <ThemeColorFields
        v-model:primary="primaryColor"
        v-model:secondary="secondaryColor"
        v-model:text="textColor"
        radio-name="textColor"
      />
      <ThemePreviewCard
        :primary="primaryColor"
        :secondary="secondaryColor"
        :text="textColor"
      />
    </section>

    <section
      class="flex flex-col gap-4"
      data-testid="theme-dark-section"
    >
      <div class="text-left font-semibold">
        Modo escuro
      </div>
      <ThemeColorFields
        v-model:primary="primaryColorDark"
        :secondary="secondaryColorDark"
        v-model:text="textColorDark"
        radio-name="textColorDark"
        @update:secondary="handleDarkSecondaryInput"
      />
      <ThemePreviewCard
        :primary="primaryColorDark"
        :secondary="secondaryColorDark"
        :text="textColorDark"
        dark
      />
    </section>

    <div class="text-xs text-gray-03 text-left">
      *As cores primária e secundária devem ter alto contraste entre elas.
    </div>
  </div>
</template>
